import React, { useMemo, useState } from "react";
import { formatMoney } from "@shopify/theme-currency";
import { withCartContext } from "./cartContext.jsx";
import Icon from "./icon.jsx";
import Image from "./image.jsx";
import clsx from "clsx";
import { generateRandomKey } from "./minicart.jsx";

const MiniCartItem = ({ item, removeItem, updateItem, changeItem, cartState, data }) => {
  const [isDetailsActive, setIsDetailsActive] = useState(false);

  const gwpItem = item?.properties["_gwp_enabled"] === "yes";
  const customizedItem = !!(item?.properties && item?.properties["Patch Count"]);
  const patchItems = useMemo(() => {
    if (customizedItem) {
      return cartState?.items.filter(
        (i) =>
          i.properties &&
          ((+i.properties["_Parent Variant"] === +item.id &&
            +i.properties["_Timestamp"] === +item?.properties["_Timestamp"]) ||
            (i.id === item.id && +i.properties["_Timestamp"] === +item?.properties["_Timestamp"]))
      );
    }
    return [];
  }, [customizedItem, cartState?.items, item.id]);

  if (item.inventory_quantity <= item.inventory_threshold) {
    if (!item.properties["__PreorderMessage"] && item.preorder_message) {
      changeItem({
        item: {
          id: item.key,
          properties: {
            ...item.properties,
            __PreorderMessage: item.preorder_message,
            "Pre-order": "Yes",
          },
        },
      });
    }
  } else {
    if (!!item.properties["__PreorderMessage"]) {
      const properties = item.properties;
      delete properties["__PreorderMessage"];
      delete properties["Pre-order"];

      changeItem({
        item: {
          id: item.key,
          properties,
        },
      });
    }
  }

  const isMonogram = item?.properties?._monogram_main_product;
  const monogramId = item?.properties?._monogram_id;
  const monogramFont = item?.properties?.["Monogram Font"];
  // const monogramPosition = item?.properties?.["Monogram Position"];
  const monogramText = item?.properties?.["Monogram Text"];
  const monogramColor = item?.properties?.["Monogram Thread Color"];
  const outlineColor = item?.properties?.["Monogram Outline Color"];
  const decorativeColor = item?.properties?.["Monogram Decorative Color"];
  const dropShadowColor = item?.properties?.["Monogram Drop Shadow Color"];
  const uploadedFile = item?.properties?.["__C Photo"];

  const monogramFeeProduct =
    monogramId &&
    cartState.items.find(
      (i) => i.properties?._monogram_id === monogramId && !i.properties._monogram_main_product
    );
  const embroideredNameId = item?.properties?._embroidered_id;
  const embroideredNameProduct =
    embroideredNameId &&
    cartState.items.find(
      (i) => i.properties?._embroidered_id === embroideredNameId && !i.properties._embroidered_main
    );

  const isBundle = item?.properties?._bundle_main_product;
  const isEmbroidered = item?.properties?._embroidered_main;
  const bundleId = item?.properties?._bundle_id;

  const isGiftCard = item?.product_type === "Gift Card";
  const gcRecipient = item?.properties?.["GC Recipient"];
  const gcMessage = item?.properties?.["GC Message"];

  const handleDetailsClick = () => {
    setIsDetailsActive(!isDetailsActive);
  };
  const handleUpdateCount = (action) => {
    let quantity = item.quantity;
    let validateQuantity = {};
    action === "plus" ? (quantity += 1) : (quantity -= 1);
    if (action === "plus") {
      validateQuantity = {
        skuToValidate: {
          [item.sku]: 1,
        },
      };
    }

    if (quantity > 0) {
      updateItem({
        item: {
          [item.key]: quantity,
          ...validateQuantity,
        },
      });
      if (action === "plus") {
        const event = new CustomEvent("cart:quantity:updated", {
          detail: {
            item: item,
            quantity: 1,
          },
        });
        document.dispatchEvent(event);
      }
    } else {
      handleRemoveItem();
    }
  };

  const handleRemoveItem = () => {
    if (customizedItem) {
      return removeItem({
        items: patchItems.reduce((acc, val) => {
          return Object.assign(acc, { [val.key]: 0 });
        }, {}),
      });
    }

    if (isMonogram) {
      return removeItem({
        items: cartState.items.reduce((acc, val) => {
          if (val.properties?._monogram_id === monogramId) {
            return Object.assign(acc, { [val.key]: 0 });
          }
          return acc;
        }, {}),
      });
    }

    if (isEmbroidered) {
      return removeItem({
        items: cartState.items.reduce((acc, val) => {
          if (val.properties?._embroidered_id === embroideredNameId) {
            return Object.assign(acc, { [val.key]: 0 });
          }
          return acc;
        }, {}),
      });
    }

    if (isBundle) {
      return removeItem({
        items: cartState.items.reduce((acc, val) => {
          if (val.properties?._bundle_id === bundleId) {
            return Object.assign(acc, { [val.key]: 0 });
          }
          return acc;
        }, {}),
      });
    }

    return removeItem({
      item: {
        [item.key]: 0,
      },
    });
  };

  const renderSubItem = (item) => {
    const formattedPrice = formatMoney(item.price);

    return (
      <div className="child-item" key={generateRandomKey(8)}>
        {(item.image || item.featured_image?.url) && (
          <img
            className="child-image"
            src={item.image || item.featured_image?.url}
            alt={null}
            loading="lazy"
            width="20"
            height="20"
          />
        )}
        <span className={clsx("text-body-2", { pink: item.pink })}>
          <span
            dangerouslySetInnerHTML={{
              __html: isMonogram
                ? `${item.title}${item.quantity > 1 ? ` (x${item.quantity})` : ""}`
                : `${item.title} - ${formattedPrice}${
                    item.quantity > 1 ? ` (x${item.quantity})` : ""
                  }`,
            }}
          />
          {item.available ? "" : " (Sold out)"}

          {(item.properties || []).length > 0
            ? item.properties.map((property, index) => (
                <div key={index}>
                  <small>
                    <em>{property}</em>
                  </small>
                </div>
              ))
            : ""}

          {item.inventory_threshold > item.inventory_quantity && item.preorder_message && (
            <span className="child-item__preorder-message--container">
              <span className="child-item__preorder-message">{item.preorder_message}</span>
            </span>
          )}
        </span>
      </div>
    );
  };

  if (
    (item.properties && item.properties["_Parent Variant"]) ||
    (monogramId && !isMonogram) ||
    (bundleId && !isBundle) ||
    (embroideredNameId && !isEmbroidered)
  ) {
    return null;
  }

  const itemVariants = item.product?.variants;

  let finalPrice = item.final_price;
  let originalPrice = item.original_price;
  let compareAtPrice = itemVariants
    ? parseInt(
        (itemVariants.find((variant) => variant.id === item.id)?.compare_at_price || "")
          .toString()
          ?.replace(".", "")
      )
    : null;

  if (isMonogram) {
    finalPrice += monogramFeeProduct.final_price;
    originalPrice += monogramFeeProduct.original_price;
    compareAtPrice += monogramFeeProduct.compare_at_price || 0;
  }

  if (isEmbroidered && embroideredNameProduct) {
    finalPrice += embroideredNameProduct.final_price;
    originalPrice += embroideredNameProduct.original_price;
    compareAtPrice += embroideredNameProduct.compare_at_price || 0;
  }

  if (customizedItem && patchItems.length > 0) {
    // Patch items includes the parent item, so just replace the price
    finalPrice = patchItems.reduce((acc, val) => acc + val.final_price, 0);
    originalPrice = patchItems.reduce((acc, val) => acc + val.original_price, 0);
    // compare_at_price is not available on the cart item, so we need to match variant
    compareAtPrice = patchItems.reduce((acc, val) => {
      const variant = val.product.variants.find((v) => v.id === val.variant_id);
      const variantCompareAtPriceString = (variant?.compare_at_price || "")
        .toString()
        ?.replace(".", "");

      return acc + parseInt(variantCompareAtPriceString || val.final_price);
    }, 0);
  }

  if (isBundle) {
    finalPrice = cartState.items.reduce((acc, val) => {
      if (val.properties?._bundle_id === bundleId) {
        return acc + val.final_line_price;
      }
      return acc;
    }, 0);
    originalPrice = cartState.items.reduce((acc, val) => {
      if (val.properties?._bundle_id === bundleId) {
        return acc + val.original_price;
      }
      return acc;
    }, 0);
    compareAtPrice = cartState.items.reduce((acc, val) => {
      if (val.properties?._bundle_id === bundleId) {
        return acc + (val.compare_at_price || 0);
      }
      return acc;
    }, 0);
  }

  const getCartList = () => {
    if (customizedItem) {
      return patchItems;
    }

    if (isEmbroidered) {
      if (!!embroideredNameProduct) {
        return [
          {
            ...embroideredNameProduct,
            title: `Embroidered Fee`,
            properties: [
              embroideredNameProduct?.properties?._embroidered_name
                ? `Embroidered Name: ${embroideredNameProduct?.properties?._embroidered_name}`
                : null,
              embroideredNameProduct?.properties?._embroidered_color
                ? `Embroidered Color: ${embroideredNameProduct?.properties?._embroidered_color}`
                : null,
            ].filter((e) => !!e),
          },
        ];
      } else {
        return [];
      }
    }

    if (isMonogram) {
      const formattedPrice = formatMoney(monogramFeeProduct.price); // Make sure you have the correct price field

      let output = [
        { title: `Text: ${monogramText}`, image: data.monogramTextImage, available: true },
        { title: `Font: ${monogramFont}`, image: data.monogramFontImage, available: true },
        {
          title: `Thread Color: ${monogramColor}`,
          image: data.monogramThreadImage,
          available: true,
        },
      ];

      if (!monogramText) output.shift();

      if (outlineColor) {
        output.push({
          title: `Outline Color: ${outlineColor}`,
          image: data.monogramThreadImage,
          available: true,
        });
      }

      if (decorativeColor) {
        output.push({
          title: `Decorative Color: ${decorativeColor}`,
          image: data?.monogramDecorativeImage,
          available: true,
        });
      }

      if (dropShadowColor) {
        output.push({
          title: `Drop Shadow Color: ${dropShadowColor}`,
          image: data?.monogramDropShadowImage,
          available: true,
        });
      }

      if (uploadedFile) {
        output.push({
          title: `Uploaded File`,
          image: uploadedFile,
          available: true,
        });
      }

      output.push({
        ...monogramFeeProduct,
        title: `Monogram fee - ${formattedPrice}`,
        image: data.monogramFeeProductImage,
      });

      return output;
    }
    if (isBundle) {
      return [
        item,
        ...cartState.items.reduce((acc, val) => {
          if (val.properties?._bundle_id === bundleId && !val.properties?._bundle_main_product) {
            return [...acc, val];
          }
          return acc;
        }, []),
      ];
    }

    return [];
  };
  const someBundleItemUnavailable = getCartList().some((item) => !item.available) && isBundle;
  const decodeLink = (input) => {
    const doc = new DOMParser().parseFromString(input, "text/html");
    return doc.documentElement.textContent;
  };

  const itemEditLink = (item, isMonogram, isBundle, isEmbroidered, style = "button") => {
    return (
      <>
        {!isMonogram && !isBundle && !isEmbroidered && (
          <a
            href={`${decodeLink(item?.properties["_Customized Bag Url"])}&timestamp=${
              item?.properties["_Timestamp"]
            }`}
            className={`${
              style === "button" ? "button button--secondary" : ""
            } mini-cart__item-edit`}
          >
            Edit
          </a>
        )}
        {isMonogram && !isBundle && (
          <a
            href={`${decodeLink(item?.properties["Monogram Url"])}&timestamp=${
              item?.properties["_Timestamp"]
            }`}
            className={`${
              style === "button" ? "button button--secondary" : ""
            } mini-cart__item-edit`}
          >
            Edit
          </a>
        )}
      </>
    );
  };

  const customProperties = () => {
    let customProperties = [];
    try {
      for (const property in item.properties) {
        if (!property.startsWith("__C ")) continue;

        const humanReadablePropertyKey = property.replace("__C ", "");
        const humanReadableProperty = item.properties[property];
        if (!humanReadableProperty) continue;

        customProperties.push(
          <div className="mini-cart__item-title text-small">
            <small className="mini-cart__item-property">
              <em>
                {humanReadablePropertyKey}:{" "}
                {humanReadablePropertyKey == "Photo" ? (
                  <img width={24} height={24} src={humanReadableProperty} />
                ) : (
                  <b>{humanReadableProperty}</b>
                )}
              </em>
            </small>
          </div>
        );
      }
    } catch (error) {
      console.warn(error);
      customProperties = [];
    }

    return customProperties;
  };

  const hasFinalSaleDiscount = (item?.discount_allocations || []).find((da) =>
    da.discount_application?.title?.toLowerCase()?.includes("final sale")
  );

  return (
    <div className="mini-cart__item" data-vid={item.id} data-mini-cart-item={gwpItem ? "gwp" : ""}>
      <div className="mini-cart__item-inner">
        {gwpItem ? (
          <>
            <div className={"mini-cart__item-image"}>
              <div className="media media--square">
                <Image {...item.featured_image} src={item.featured_image?.url} maxWidth={200} />
              </div>
            </div>
            <div className="mini-cart__item-content">
              <div className="mini-cart__item-title text-small">
                <span>{item.product_title}</span>
                {item.product.tags && item.product.tags.includes("excluded-promo") && (
                  <div>
                    <span style={{ fontWeight: 600, fontSize: "14px" }}>Excluded from Promo</span>
                  </div>
                )}
                <br />
                <small className="mini-cart__item-property">
                  <em>{`${item.properties["GWP"]}`}</em>
                </small>
              </div>

              {!item.product_has_only_default_variant && item.variant_title !== "Default Title" && (
                <div className="mini-cart__item-color text-body-2">
                  {`${item.options_with_values[0]?.value}`}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div
              className={`mini-cart__item-image ${
                item.properties?.__gwp == "true" ? "mini-cart__item-image--gwp" : ""
              }`}
            >
              <a href={item.url}>
                <div className={customizedItem ? "media media--rectangle" : "media media--square"}>
                  {customizedItem || isMonogram ? (
                    <Image src={item.properties["_Screenshot"]} maxWidth={200} />
                  ) : (
                    <Image {...item.featured_image} src={item.featured_image?.url} maxWidth={200} />
                  )}
                </div>
              </a>
            </div>
            <div className="mini-cart__item-content">
              <div className="mini-cart__item-title text-small">
                <a href={item.url}>{item.product_title}</a>
                {item.product.tags && item.product.tags.includes("excluded-promo") && (
                  <div>
                    <span style={{ fontWeight: 600, fontSize: "14px" }}>Excluded from Promo</span>
                  </div>
                )}
                {customizedItem && <div style={{ fontWeight: "bold" }}>Custom Product</div>}
                {isMonogram && <div style={{ fontWeight: "bold" }}>Monogrammed</div>}
              </div>
              <div className="mini-cart__item-price text-utility">
                {item.discounts.length > 0 ? (
                  <>
                    <div>
                      <span className="new-price">{formatMoney(finalPrice)}</span>
                      <span className="old-price">{formatMoney(originalPrice)}</span>
                    </div>
                    {item.discount_allocations.map((da) => {
                      return (
                        <div
                          className="mini-cart__item-discount text-utility"
                          key={generateRandomKey(8)}
                        >
                          {da.discount_application?.title}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {compareAtPrice > finalPrice ? (
                      <div>
                        <span className="new-price">{formatMoney(finalPrice)}</span>
                        <span className="old-price">{formatMoney(compareAtPrice)}</span>
                      </div>
                    ) : (
                      formatMoney(finalPrice)
                    )}
                  </>
                )}
              </div>

              {!item.available && (
                <div class="text-body-2-medium text-error">
                  {data.locales.item_soldout
                    .split("[edit]")
                    .flatMap((i) => [i, <a href={item.url}>Edit</a>])
                    .slice(0, -1)}
                </div>
              )}

              {!isGiftCard &&
                !item.product_has_only_default_variant &&
                item.variant_title !== "Default Title" && (
                  <div className="mini-cart__item-color text-body-2">
                    {item.options_with_values.map((option, index) => {
                      return (
                        <span key={index}>
                          {option.value}
                          {index < item.options_with_values.length - 1 ? " / " : ""}
                        </span>
                      );
                    })}
                  </div>
                )}
              {isGiftCard && (
                <>
                  <small className="mini-cart__item-property">
                    <em> Recipient's email: {gcRecipient}</em>
                    <br />
                    <em>Message: {gcMessage}</em>
                  </small>
                </>
              )}

              {!!item.properties["__PreorderMessage"] && (
                <div className="mini-cart__item-title text-small pre-order-message">
                  <small
                    style={{ color: "var(--color-brand-error)" }}
                    className="mini-cart__item-property"
                  >
                    <em>{item.properties["__PreorderMessage"]}</em>
                  </small>
                </div>
              )}
              {(item.properties["_LPROP"] == "Final Sale" ||
                hasFinalSaleDiscount ||
                item.properties["__FinalSale"] == "Yes") && (
                <div className="mini-cart__item-title text-small">
                  <small
                    className="mini-cart__item-property"
                    style={{ color: "var(--color-brand-error)" }}
                  >
                    FINAL SALE
                  </small>
                </div>
              )}
              {item.properties["__ProductMessaging"] && (
                <div className="mini-cart__item-title text-small">
                  <small
                    className="mini-cart__item-property"
                    style={{ color: "var(--color-brand-error)" }}
                  >
                    {item.properties["__ProductMessaging"]}
                  </small>
                </div>
              )}
              {customProperties()}

              {item.properties?._embroidered_name &&
                (!isEmbroidered || (isEmbroidered && !embroideredNameProduct)) && (
                  <div className="mini-cart__item-title text-small">
                    <small className="mini-cart__item-property">
                      <em>Embroidered Name: {item.properties._embroidered_name}</em>
                    </small>
                  </div>
                )}
              {item.properties?._embroidered_color &&
                (!isEmbroidered || (isEmbroidered && !embroideredNameProduct)) && (
                  <div className="mini-cart__item-title text-small">
                    <small className="mini-cart__item-property">
                      <em>Embroidered Thread Color: {item.properties._embroidered_color}</em>
                    </small>
                  </div>
                )}
              {!customizedItem &&
                !isMonogram &&
                !isBundle &&
                !isEmbroidered &&
                !(item.properties?.__gwp == "true") && (
                  <>
                    {!isGiftCard && (
                      <div className="mini-cart__item-quantity">
                        <button
                          type="button"
                          className="button--transparent flex flex--center decrease-button"
                          onClick={() => handleUpdateCount("minus")}
                          aria-label="Decrease Quantity"
                        >
                          <Icon id={"minus"} />
                        </button>
                        <div className="mini-cart__item-count flex flex--center">
                          {item.quantity}
                        </div>
                        <button
                          type="button"
                          className="button--transparent flex flex--center increase-button"
                          onClick={() => handleUpdateCount("plus")}
                          aria-label="Increase Quantity"
                        >
                          <Icon id={"plus"} />
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      className={`button button--secondary mini-cart__item-remove`}
                      onClick={handleRemoveItem}
                      aria-label="Remove Item from Cart"
                      value="Remove Item from Cart"
                    >
                      <Icon id={"remove"} />
                    </button>
                  </>
                )}
              {someBundleItemUnavailable && (
                <div className="text-body-2-medium text-error">
                  {data.locales.bundle_item_soldout
                    .split("[edit]")
                    .flatMap((i) => [
                      i,
                      itemEditLink(item, isMonogram, isBundle, isEmbroidered, "link"),
                    ])
                    .slice(0, -1)}
                </div>
              )}
              {(customizedItem || isMonogram || isBundle || isEmbroidered) && (
                <>
                  {((patchItems && patchItems.length > 0) ||
                    isMonogram ||
                    isBundle ||
                    isEmbroidered) &&
                    getCartList().length > 0 && (
                      <>
                        <div className="details">
                          <div className={`children-dropdown ${isDetailsActive ? "active" : ""}`}>
                            <div className="toggle" onClick={handleDetailsClick}>
                              <span>Details</span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 9l-7 7-7-7"
                                ></path>
                              </svg>
                            </div>
                            <div className="list">
                              {getCartList().filter(Boolean).map(renderSubItem)}
                              {customizedItem &&
                                item.properties &&
                                item.properties["Tidy Patches"] && (
                                  <div className="child-item text-body-2">{`Message: ${item.properties["Tidy Patches"]}`}</div>
                                )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  <div className="mini-cart__item-edit">
                    {itemEditLink(item, isMonogram, isBundle, isEmbroidered)}
                  </div>
                  <button
                    type="button"
                    className={`button button--secondary mini-cart__item-remove`}
                    onClick={handleRemoveItem}
                    aria-label="Remove Item from Cart"
                    value="Remove Item from Cart"
                  >
                    <Icon id={"remove"} />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default withCartContext(MiniCartItem);
